import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { TranslationEntry, PageResponse, CreateTranslationRequest, BulkImportResult } from '../types';
import { useAuth } from '../hooks/useAuth';

const CONTEXT_OPTIONS = ['', 'general', 'booking', 'garage', 'notification', 'profile', 'payment', 'review', 'error'];
const PAGE_SIZES = [20, 50, 100];

interface Toast {
  message: string;
  type: 'success' | 'error';
}

interface EditingCell {
  id: string;
  field: 'englishValue' | 'arabicValue' | 'context';
}

function ContextBadge({ context }: { context: string | null }) {
  if (!context) return <span className="admin-text-muted">-</span>;
  return <span className="admin-context-badge">{context}</span>;
}

export default function TranslationsPage() {
  const auth = useAuth();

  // Data
  const [translations, setTranslations] = useState<TranslationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [contextFilter, setContextFilter] = useState('');
  const [missingArabicOnly, setMissingArabicOnly] = useState(false);

  // Inline editing
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTranslationRequest>({ translationKey: '', englishValue: '', arabicValue: '', context: '' });
  const [createLoading, setCreateLoading] = useState(false);

  // Bulk import
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchTranslations = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api.listTranslations(auth.token, {
      search: searchText.trim() || undefined,
      context: contextFilter || undefined,
      missingArabic: missingArabicOnly || undefined,
      page,
      size: pageSize,
    })
      .then((data: PageResponse<TranslationEntry>) => {
        setTranslations(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, searchText, contextFilter, missingArabicOnly, page, pageSize]);

  // Debounced fetch on filter changes
  useEffect(() => {
    const timeout = setTimeout(fetchTranslations, 300);
    return () => clearTimeout(timeout);
  }, [fetchTranslations]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchText, contextFilter, missingArabicOnly, pageSize]);

  // Focus edit input
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingCell]);

  // --- Inline Edit ---
  const startEdit = (entry: TranslationEntry, field: EditingCell['field']) => {
    setEditingCell({ id: entry.id, field });
    setEditValue((entry[field] as string) || '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell || !auth.token) return;
    const entry = translations.find((t) => t.id === editingCell.id);
    if (!entry) return;

    const currentValue = (entry[editingCell.field] as string) || '';
    if (editValue === currentValue) {
      cancelEdit();
      return;
    }

    try {
      const updateData = { [editingCell.field]: editValue || undefined };
      const updated = await api.updateTranslation(editingCell.id, updateData, auth.token);
      setTranslations((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      showToast('Translation updated', 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Update failed', 'error');
    }
    cancelEdit();
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // --- Create ---
  const handleCreate = async () => {
    if (!auth.token) return;
    if (!createForm.translationKey.trim() || !createForm.englishValue.trim()) {
      showToast('Key and English value are required', 'error');
      return;
    }
    setCreateLoading(true);
    try {
      await api.createTranslation({
        translationKey: createForm.translationKey.trim(),
        englishValue: createForm.englishValue.trim(),
        arabicValue: createForm.arabicValue?.trim() || undefined,
        context: createForm.context?.trim() || undefined,
      }, auth.token);
      showToast('Translation created', 'success');
      setShowCreateModal(false);
      setCreateForm({ translationKey: '', englishValue: '', arabicValue: '', context: '' });
      fetchTranslations();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Create failed', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deleteId || !auth.token) return;
    try {
      await api.deleteTranslation(deleteId, auth.token);
      showToast('Translation deleted', 'success');
      setDeleteId(null);
      fetchTranslations();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  // --- Bulk Import ---
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBulkText((ev.target?.result as string) || '');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkImport = async () => {
    if (!auth.token || !bulkText.trim()) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const parsed = JSON.parse(bulkText.trim());
      const entries = Array.isArray(parsed) ? parsed : parsed.entries;
      if (!Array.isArray(entries) || entries.length === 0) {
        showToast('Invalid format: expected an array of translation entries', 'error');
        setBulkLoading(false);
        return;
      }
      const result = await api.bulkImportTranslations({ entries }, auth.token);
      setBulkResult(result);
      if (result.failed === 0) {
        showToast(`Imported: ${result.created} created, ${result.updated} updated`, 'success');
      } else {
        showToast(`Import completed with ${result.failed} errors`, 'error');
      }
      fetchTranslations();
    } catch (err: unknown) {
      if (err instanceof SyntaxError) {
        showToast('Invalid JSON format', 'error');
      } else {
        showToast(err instanceof Error ? err.message : 'Import failed', 'error');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  // --- Pagination ---
  const pageNumbers = () => {
    const pages: number[] = [];
    const start = Math.max(0, page - 2);
    const end = Math.min(totalPages - 1, page + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>
        Translations {!loading && `(${totalElements})`}
      </h2>

      <div className="admin-card">
        {/* Toolbar */}
        <div className="admin-toolbar">
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by key or value..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <select
            className="admin-select"
            value={contextFilter}
            onChange={(e) => setContextFilter(e.target.value)}
          >
            <option value="">All Contexts</option>
            {CONTEXT_OPTIONS.filter(Boolean).map((ctx) => (
              <option key={ctx} value={ctx}>{ctx}</option>
            ))}
          </select>
          <label className="admin-toggle-label">
            <input
              type="checkbox"
              className="admin-toggle-checkbox"
              checked={missingArabicOnly}
              onChange={(e) => setMissingArabicOnly(e.target.checked)}
            />
            <span className="admin-toggle-switch" />
            <span className="admin-toggle-text">Missing Arabic</span>
          </label>
          <button className="admin-btn admin-btn-primary" onClick={() => setShowCreateModal(true)}>
            + Add Translation
          </button>
          <button className="admin-btn admin-btn-outline" onClick={() => { setShowBulkModal(true); setBulkResult(null); setBulkText(''); }}>
            Bulk Import
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : translations.length === 0 ? (
          <div className="admin-empty-state">
            <p>No translations found.</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>English</th>
                    <th>Arabic</th>
                    <th>Context</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {translations.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {entry.translationKey}
                      </td>

                      {/* English - editable */}
                      <td
                        className={`admin-editable-cell${editingCell?.id === entry.id && editingCell.field === 'englishValue' ? ' editing' : ''}`}
                        onClick={() => !editingCell && startEdit(entry, 'englishValue')}
                      >
                        {editingCell?.id === entry.id && editingCell.field === 'englishValue' ? (
                          <div className="admin-inline-edit">
                            <input
                              ref={editInputRef}
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={handleEditKeyDown}
                              className="admin-inline-edit-input"
                            />
                          </div>
                        ) : (
                          <span className="admin-editable-text">{entry.englishValue}</span>
                        )}
                      </td>

                      {/* Arabic - editable, RTL */}
                      <td
                        className={`admin-editable-cell${editingCell?.id === entry.id && editingCell.field === 'arabicValue' ? ' editing' : ''}`}
                        onClick={() => !editingCell && startEdit(entry, 'arabicValue')}
                      >
                        {editingCell?.id === entry.id && editingCell.field === 'arabicValue' ? (
                          <div className="admin-inline-edit">
                            <input
                              ref={editInputRef}
                              type="text"
                              dir="rtl"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={handleEditKeyDown}
                              className="admin-inline-edit-input"
                            />
                          </div>
                        ) : (
                          <span className="admin-editable-text" dir="rtl" style={{ textAlign: 'right', display: 'block' }}>
                            {entry.arabicValue || <span className="admin-text-muted">-</span>}
                          </span>
                        )}
                      </td>

                      <td><ContextBadge context={entry.context} /></td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>
                        {entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <button
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => setDeleteId(entry.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
              </div>
              <div className="admin-pagination-controls">
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                {pageNumbers().map((p) => (
                  <button
                    key={p}
                    className={`admin-pagination-btn${p === page ? ' active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </button>
                ))}
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
              <select
                className="admin-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s} / page</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add Translation</h3>
              <button className="admin-modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Translation Key *</label>
                <input
                  type="text"
                  className="admin-form-input"
                  placeholder="e.g. booking.confirm.title"
                  value={createForm.translationKey}
                  onChange={(e) => setCreateForm({ ...createForm, translationKey: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">English Value *</label>
                <input
                  type="text"
                  className="admin-form-input"
                  placeholder="English text"
                  value={createForm.englishValue}
                  onChange={(e) => setCreateForm({ ...createForm, englishValue: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Arabic Value</label>
                <input
                  type="text"
                  className="admin-form-input"
                  dir="rtl"
                  placeholder="Arabic text"
                  value={createForm.arabicValue}
                  onChange={(e) => setCreateForm({ ...createForm, arabicValue: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Context</label>
                <select
                  className="admin-form-input"
                  value={createForm.context}
                  onChange={(e) => setCreateForm({ ...createForm, context: e.target.value })}
                >
                  <option value="">None</option>
                  {CONTEXT_OPTIONS.filter(Boolean).map((ctx) => (
                    <option key={ctx} value={ctx}>{ctx}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={handleCreate} disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="admin-modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="admin-modal admin-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Bulk Import Translations</h3>
              <button className="admin-modal-close" onClick={() => setShowBulkModal(false)}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', marginBottom: '0.75rem' }}>
                Paste a JSON array of translations or upload a JSON file. Each entry needs: <code>translationKey</code>, <code>englishValue</code>, and optionally <code>arabicValue</code>, <code>context</code>.
              </p>
              <textarea
                className="admin-textarea"
                rows={10}
                placeholder='[{"translationKey": "app.title", "englishValue": "Garagat", "arabicValue": "قراقات"}]'
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
              />
              <div style={{ marginTop: '0.75rem' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => fileInputRef.current?.click()}>
                  Upload JSON File
                </button>
              </div>

              {bulkResult && (
                <div className="admin-bulk-result" style={{ marginTop: '1rem' }}>
                  <div className="admin-bulk-result-stats">
                    <span className="admin-bulk-stat success">{bulkResult.created} created</span>
                    <span className="admin-bulk-stat info">{bulkResult.updated} updated</span>
                    {bulkResult.failed > 0 && <span className="admin-bulk-stat error">{bulkResult.failed} failed</span>}
                  </div>
                  {bulkResult.errors.length > 0 && (
                    <div className="admin-bulk-errors">
                      {bulkResult.errors.map((err, i) => (
                        <div key={i} className="admin-bulk-error-item">{err}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => setShowBulkModal(false)}>Close</button>
              <button className="admin-btn admin-btn-primary" onClick={handleBulkImport} disabled={bulkLoading || !bulkText.trim()}>
                {bulkLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="admin-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Delete Translation</h3>
              <button className="admin-modal-close" onClick={() => setDeleteId(null)}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <p>Are you sure you want to delete this translation? This action cannot be undone.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-outline" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`admin-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
